import {useState} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  Divider,
  Typography,
  FormControl,
  FormControlLabel
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {SchemaRepository} from "../../../../repositories/SchemaRepository";

const PROPERTY_TYPES = ["string", "int", "float", "bool", "datetime", "list", "dict"];

export const CreateSchemaModal = ({ open, onClose, onSuccess }) => {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");

  // Properties
  const [properties, setProperties] = useState([]);

  const handleAddProperty = () => {
    setProperties([...properties, {
      name: "",
      type: "string",
      required: false,
      default: "",
      description: "",
      sampleValue: ""
    }]);
  };

  const handleRemoveProperty = (index) => {
    setProperties(properties.filter((_, i) => i !== index));
  };

  const handlePropertyChange = (index, field, value) => {
    const newProperties = [...properties];
    newProperties[index][field] = value;
    setProperties(newProperties);
  };

  const handleClose = () => {
    // Reset form
    setLabel("");
    setDescription("");
    setProperties([]);
    onClose();
  };

  const parseSampleValue = (value, type) => {
    if (!value || value.trim() === "") {
      return "";
    }

    try {
      switch (type) {
        case "int":
          const intVal = parseInt(value, 10);
          if (isNaN(intVal)) throw new Error("Invalid integer");
          return intVal;
        case "float":
          const floatVal = parseFloat(value);
          if (isNaN(floatVal)) throw new Error("Invalid float");
          return floatVal;
        case "bool":
          const lowerVal = value.toLowerCase();
          if (lowerVal === "true" || lowerVal === "1") return true;
          if (lowerVal === "false" || lowerVal === "0") return false;
          throw new Error("Invalid boolean (use true/false or 1/0)");
        case "list":
          return JSON.parse(value);
        case "dict":
          return JSON.parse(value);
        case "datetime":
          // Keep as string, can be parsed on backend
          return value;
        case "string":
        default:
          return value;
      }
    } catch (error) {
      throw new Error(`Invalid ${type} value: ${error.message}`);
    }
  };

  const handleCreate = async () => {
    try {
      // Validate required fields with sample values
      const missingRequired = properties.filter(prop => prop.required && !prop.sampleValue);
      if (missingRequired.length > 0) {
        alert(`Required properties must have a sample value: ${missingRequired.map(p => p.name).join(', ')}`);
        return;
      }

      const propertiesObj = {};
      for (const prop of properties) {
        if (prop.name) {
          try {
            let parsedSampleValue = "";
            if (prop.sampleValue) {
              parsedSampleValue = parseSampleValue(prop.sampleValue, prop.type);
            } else if (prop.required) {
              alert(`Required property '${prop.name}' must have a sample value`);
              return;
            }

            propertiesObj[prop.name] = {
              type: prop.type,
              required: prop.required,
              default: prop.default || null,
              description: prop.description,
              sampleValue: parsedSampleValue
            };
          } catch (error) {
            alert(`Error in property '${prop.name}': ${error.message}`);
            return;
          }
        }
      }

      await SchemaRepository.createNodeSchema(label, propertiesObj, description);

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error creating schema:", error);
      alert("Failed to create schema: " + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Node Schema</DialogTitle>
      <DialogContent sx={{ maxHeight: '60dvh', overflowY: 'auto' }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Label"
            fullWidth
            required
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="The node label/type (e.g., Person, Product)"
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">Properties</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddProperty}
                size="small"
              >
                Add Property
              </Button>
            </Stack>

            <Stack spacing={2}>
              {properties.map((prop, index) => (
                <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <TextField
                        label="Property Name"
                        size="small"
                        required
                        value={prop.name}
                        onChange={(e) => handlePropertyChange(index, 'name', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={prop.type}
                          label="Type"
                          onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                        >
                          {PROPERTY_TYPES.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveProperty(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Default Value"
                        size="small"
                        value={prop.default}
                        onChange={(e) => handlePropertyChange(index, 'default', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="Sample Value"
                        size="small"
                        value={prop.sampleValue}
                        onChange={(e) => handlePropertyChange(index, 'sampleValue', e.target.value)}
                        required={prop.required}
                        error={prop.required && !prop.sampleValue}
                        helperText={prop.required && !prop.sampleValue ? "Required" : "Value used when creating data"}
                        fullWidth
                      />
                    </Stack>

                    <TextField
                      label="Description"
                      size="small"
                      value={prop.description}
                      onChange={(e) => handlePropertyChange(index, 'description', e.target.value)}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={prop.required}
                          onChange={(e) => handlePropertyChange(index, 'required', e.target.checked)}
                        />
                      }
                      label="Required"
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="ghost" onClick={handleClose}>Cancel</Button>
        <Button
          variant="modal"
          onClick={handleCreate}
          disabled={!label}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};